/**
 * wishlist-popover.js — loaded eagerly by scripts.liquid (it only defines
 * functions and registers window.__wlPopover; no work runs until opened).
 *
 * Local-first: rows render instantly from the full-item cache handed over by
 * wishlist.js (ctx.getItems()), so opening never waits on the network. GET /me
 * then reconciles in the background — it refreshes the rows only if the server
 * disagrees with the cache, and it is what enables the Share button (the
 * share_id lives server-side). Share uses the Web Share API with a clipboard
 * fallback, exposing only the share_id.
 */

let panel = null;
let openState = false;
let currentCtx = null;
let shareId = null;

const CSS = `
.wl-popover{position:fixed;z-index:1000;width:min(23rem,calc(100vw - 1rem));max-height:min(70vh,34rem);display:flex;flex-direction:column;background:var(--color-background,#fff);color:var(--color-foreground,#111);border:1px solid rgba(0,0,0,.12);border-radius:var(--style-border-radius-popover,8px);box-shadow:0 8px 30px rgba(0,0,0,.16);overflow:hidden}
.wl-popover__head{display:flex;align-items:center;justify-content:space-between;gap:.5rem;padding:.75rem 1rem;border-bottom:1px solid rgba(0,0,0,.08)}
.wl-popover__title{font-weight:600;font-size:.95rem;margin:0}
.wl-popover__close{background:none;border:0;cursor:pointer;padding:.25rem;line-height:0;color:inherit}
.wl-popover__body{overflow-y:auto;overscroll-behavior:contain;padding:.25rem 0;flex:1}
.wl-row{display:flex;align-items:center;gap:.75rem;padding:.5rem 1rem;text-decoration:none;color:inherit}
.wl-row__img{width:48px;height:48px;object-fit:cover;border-radius:6px;background:rgba(0,0,0,.05);flex-shrink:0;display:block}
.wl-row__info{flex:1;min-width:0}
.wl-row__title{font-size:.85rem;line-height:1.25;overflow:hidden;text-overflow:ellipsis;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;text-decoration:none;color:inherit}
.wl-row__price{font-size:.8rem;opacity:.7;margin-top:.15rem}
.wl-row__remove{background:none;border:0;cursor:pointer;padding:.4rem;line-height:0;color:inherit;opacity:.55;flex-shrink:0}
.wl-row__remove:hover{opacity:1}
.wl-row__actions{display:flex;align-items:center;gap:.25rem;flex-shrink:0}
.wl-row__cta{font-family:inherit;display:inline-flex;align-items:center;justify-content:center;border:1px solid currentColor;border-radius:var(--style-border-radius-buttons-primary,4px);background:none;color:inherit;cursor:pointer;text-decoration:none;transition:background-color .15s ease,color .15s ease}
.wl-row__cta:hover{background:var(--color-foreground,#111);color:var(--color-background,#fff)}
.wl-row__cta[disabled]{opacity:.5;cursor:default;pointer-events:none}
.wl-row__cta.is-added{background:var(--color-foreground,#111);color:var(--color-background,#fff)}
.wl-row__cta--icon{width:1.75rem;height:1.75rem;padding:0;flex-shrink:0}
.wl-row__cta--icon svg{width:1rem;height:1rem;display:block}
.wl-row__cta--icon .svg-wrapper{display:inline-flex;line-height:0}
.wl-row__cta--choose{min-width:3.4rem;padding:.35rem .6rem;font-size:.72rem;letter-spacing:.06em;text-transform:uppercase;white-space:nowrap}
.wl-popover__foot{display:flex;gap:.5rem;padding:.75rem 1rem;border-top:1px solid rgba(0,0,0,.08)}
.wl-popover__btn{font-family:inherit;flex:1;display:inline-flex;align-items:center;justify-content:center;gap:.4rem;padding:.55rem .75rem;font-size:.85rem;border-radius:var(--style-border-radius-buttons-primary,6px);border:1px solid currentColor;background:none;color:inherit;cursor:pointer;text-decoration:none}
.wl-popover__btn--primary{background:var(--color-primary-button-background,#111);color:var(--color-primary-button-text,#fff);border-color:transparent}
.wl-popover__empty{padding:2rem 1.5rem;text-align:center}
.wl-popover__empty-icon{display:block;width:26px;height:26px;margin:0 auto .75rem;opacity:.3}
.wl-popover__empty-icon svg{display:block;width:100%;height:100%}
.wl-popover__empty-title{font-size:.9rem;font-weight:600;margin:0 0 .3rem}
.wl-popover__empty-hint{font-size:.82rem;opacity:.65;margin:0;line-height:1.5;max-width:16rem;margin-inline:auto}
@media (max-width:749px){.wl-popover{left:.5rem !important;right:.5rem !important;width:auto}}
`;

function esc(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

function injectStyles() {
  if (document.getElementById('wl-popover-styles')) return;
  const style = document.createElement('style');
  style.id = 'wl-popover-styles';
  style.textContent = CSS;
  document.head.appendChild(style);
}

// Liquid's `t` filter HTML-escapes locale strings (e.g. ' -> &#39;) even
// inside the JSON config, so decode entities here; esc() re-escapes safely
// wherever strings are inserted as HTML.
function decodeEntities(s) {
  if (typeof s !== 'string' || s.indexOf('&') === -1) return s;
  const el = document.createElement('textarea');
  el.innerHTML = s;
  return el.value;
}

function i18n(ctx, key, fallback) {
  return decodeEntities((ctx.cfg.i18n && ctx.cfg.i18n[key]) || fallback);
}

function productUrl(ctx, item) {
  const root = (ctx.cfg.rootUrl || '/').replace(/\/$/, '');
  let url = root + '/products/' + encodeURIComponent(item.handle || '');
  if (item.variant_id) url += '?variant=' + encodeURIComponent(item.variant_id);
  return url;
}

function rowHTML(item, ctx) {
  const url = item.handle ? productUrl(ctx, item) : '#';
  // "Choose" (link to PDP) for multi-variant / personalisable products,
  // icon-only one-tap Add for everything else — mirrors the cart addon cards
  // (same icon-add-to-cart.svg, injected via WishlistConfig.icons.add).
  const addIcon = (ctx.cfg.icons && ctx.cfg.icons.add) || null;
  const cta = item.choose
    ? `<a class="wl-row__cta wl-row__cta--choose" href="${esc(url)}">${esc(i18n(ctx, 'choose', 'Choose'))}</a>`
    : `<button type="button" class="wl-row__cta${addIcon ? ' wl-row__cta--icon' : ''}" data-wl-add aria-label="${esc(i18n(ctx, 'add', 'Add'))}">` +
      `${addIcon ? `<span aria-hidden="true" class="svg-wrapper add-to-cart-icon">${addIcon}</span>` : esc(i18n(ctx, 'add', 'Add'))}</button>`;
  return `
    <div class="wl-row" data-wl-row data-variant-id="${esc(item.variant_id)}" data-product-id="${esc(item.product_id)}">
      <a href="${esc(url)}" tabindex="-1" aria-hidden="true">
        ${item.image ? `<img class="wl-row__img" src="${esc(item.image)}" alt="" loading="lazy" width="48" height="48">` : '<span class="wl-row__img"></span>'}
      </a>
      <div class="wl-row__info">
        <a class="wl-row__title" href="${esc(url)}">${esc(item.title || item.handle || '')}</a>
        ${item.price ? `<div class="wl-row__price">${esc(item.price)}</div>` : ''}
      </div>
      <div class="wl-row__actions">
        ${cta}
        <button type="button" class="wl-row__remove" data-wl-remove aria-label="${esc(i18n(ctx, 'remove', 'Remove'))}">
          <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M4 4l12 12M16 4L4 16"/></svg>
        </button>
      </div>
    </div>`;
}

/**
 * One-tap add to cart, wired into the theme's native cart event flow
 * (CartLinesUpdateEvent from the standard-events bundle) so the header
 * cart bubble and cart drawer update exactly like any other add.
 */
const CHECK_ICON = '<svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M4 10.5l4 4 8-9"/></svg>';

async function addToCart(item, btn, ctx) {
  btn.disabled = true;
  const isIcon = btn.classList.contains('wl-row__cta--icon');
  const origHTML = btn.innerHTML;
  let Ev = null;
  let deferred = null;
  try {
    try {
      // Same bundle the theme's importmap maps '@shopify/events' to.
      const mod = await import('https://cdn.shopify.com/storefront/standard-events.js');
      Ev = mod.CartLinesUpdateEvent;
    } catch (e) { /* events unavailable: still do the add */ }

    if (Ev) {
      deferred = Ev.createPromise();
      document.body.dispatchEvent(new Ev({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(item.variant_id), quantity: 1 }],
        promise: deferred.promise,
      }));
    }

    // Include cart section ids so the cart drawer can morph, like product-form does.
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });

    const addUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
    const payload = { id: Number(item.variant_id), quantity: 1 };
    if (sectionIds.length) {
      payload.sections = sectionIds.join(',');
      payload.sections_url = window.location.pathname;
    }

    const res = await fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'add failed');

    // The sections just returned carry the cart JSON (see cart-items-component.liquid),
    // so the drawer does not wait on a second round trip to see the new line.
    const { readCartFromSections } = await import('@theme/product-form').catch(() => ({}));
    const ajaxCart =
      (readCartFromSections && readCartFromSections(data.sections)) ||
      (await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json()));
    if (deferred) {
      deferred.resolve({
        cart: Ev.createCartFromAjaxResponse
          ? Ev.createCartFromAjaxResponse(ajaxCart)
          : { totalQuantity: ajaxCart.item_count },
        detail: {
          items: ajaxCart.items,
          source: 'wishlist-popover',
          sourceId: 'wishlist-popover',
          itemCount: 1,
          productId: String(item.product_id),
          sections: data.sections,
          didError: false,
        },
      });
    }

    // Success feedback: checkmark in the icon square (or "Added" text fallback)
    btn.innerHTML = isIcon ? CHECK_ICON : esc(i18n(ctx, 'added', 'Added'));
    btn.classList.add('is-added');
    btn.setAttribute('aria-label', i18n(ctx, 'added', 'Added'));
    setTimeout(() => {
      btn.innerHTML = origHTML;
      btn.classList.remove('is-added');
      btn.setAttribute('aria-label', i18n(ctx, 'add', 'Add'));
      btn.disabled = false;
    }, 1600);
  } catch (err) {
    if (deferred) deferred.reject(err);
    console.warn('[wishlist] add to cart failed:', err);
    btn.innerHTML = esc(i18n(ctx, 'addError', 'Error'));
    setTimeout(() => {
      btn.innerHTML = origHTML;
      btn.disabled = false;
    }, 1600);
  }
}

const HEART_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M12 21c-4.8-3.4-8-6.4-8-10a4.6 4.6 0 0 1 8-3.1A4.6 4.6 0 0 1 20 11c0 3.6-3.2 6.6-8 10z"/></svg>';

function render(items, ctx) {
  const body = panel.querySelector('[data-wl-body]');
  if (!items.length) {
    // The empty state teaches rather than dead-ends: heart icon (the same
    // shape as the buttons on every product), state, one-line how-to. The
    // icon sits above the text in its own sized box — inlining it into the
    // sentence lets the theme's global svg styles blow the line apart.
    body.innerHTML = `
      <div class="wl-popover__empty">
        <span class="wl-popover__empty-icon" aria-hidden="true">${HEART_ICON}</span>
        <p class="wl-popover__empty-title">${esc(i18n(ctx, 'empty', 'Your wishlist is empty'))}</p>
        <p class="wl-popover__empty-hint">${esc(i18n(ctx, 'emptyHint', 'Tap the heart on any product to save it here.'))}</p>
      </div>`;
    return;
  }
  body.innerHTML = items.map((i) => rowHTML(i, ctx)).join('');
}

function position(anchor) {
  const r = anchor.getBoundingClientRect();
  panel.style.top = Math.round(r.bottom + 8) + 'px';
  panel.style.right = Math.max(8, Math.round(window.innerWidth - r.right - 8)) + 'px';
  panel.style.left = 'auto';
}

function close() {
  if (panel) panel.remove();
  panel = null;
  openState = false;
  document.removeEventListener('click', onDocClick, true);
  document.removeEventListener('keydown', onKeydown);
}

function onDocClick(e) {
  if (!panel) return;
  if (panel.contains(e.target)) return;
  if (e.target.closest && e.target.closest('[data-wishlist-open]')) return; // handled by toggle
  close();
}

function onKeydown(e) {
  if (e.key === 'Escape') close();
}

async function share(ctx, btn) {
  if (!shareId) return;
  const url = ctx.cfg.pageUrl + '?w=' + shareId;
  const abs = new URL(url, window.location.origin).href;
  const title = i18n(ctx, 'shareTitle', 'My wishlist');
  if (navigator.share) {
    try { await navigator.share({ title, url: abs }); return; } catch (e) { if (e.name === 'AbortError') return; }
  }
  try {
    await navigator.clipboard.writeText(abs);
    const orig = btn.textContent;
    btn.textContent = i18n(ctx, 'copied', 'Link copied');
    setTimeout(() => { btn.textContent = orig; }, 1800);
  } catch (e) { window.prompt(i18n(ctx, 'copyPrompt', 'Copy this link'), abs); }
}

export function toggle(anchor, ctx) {
  if (openState) { close(); return; }
  open(anchor, ctx);
}

export function open(anchor, ctx) {
  injectStyles();
  currentCtx = ctx;
  close();
  openState = true;

  panel = document.createElement('div');
  panel.className = 'wl-popover color-scheme-1';
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-label', i18n(ctx, 'title', 'Wishlist'));
  panel.innerHTML = `
    <div class="wl-popover__head">
      <p class="wl-popover__title">${esc(i18n(ctx, 'title', 'Wishlist'))}</p>
      <button type="button" class="wl-popover__close" data-wl-close aria-label="${esc(i18n(ctx, 'close', 'Close'))}">
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M4 4l12 12M16 4L4 16"/></svg>
      </button>
    </div>
    <div class="wl-popover__body" data-wl-body>
      <div class="wl-popover__empty">…</div>
    </div>
    <div class="wl-popover__foot">
      <a class="wl-popover__btn" href="${esc(ctx.cfg.pageUrl)}">${esc(i18n(ctx, 'viewAll', 'View all'))}</a>
      <button type="button" class="wl-popover__btn wl-popover__btn--primary" data-wl-share disabled>${esc(i18n(ctx, 'share', 'Share'))}</button>
    </div>`;
  document.body.appendChild(panel);
  position(anchor);

  panel.addEventListener('click', (e) => {
    const closeBtn = e.target.closest('[data-wl-close]');
    if (closeBtn) { close(); return; }
    const shareBtn = e.target.closest('[data-wl-share]');
    if (shareBtn) { share(currentCtx, shareBtn); return; }
    const addBtn = e.target.closest('[data-wl-add]');
    if (addBtn) {
      const row = addBtn.closest('[data-wl-row]');
      addToCart({
        variant_id: row.getAttribute('data-variant-id'),
        product_id: row.getAttribute('data-product-id'),
      }, addBtn, currentCtx);
      return;
    }
    const removeBtn = e.target.closest('[data-wl-remove]');
    if (removeBtn) {
      const row = removeBtn.closest('[data-wl-row]');
      const v = row.getAttribute('data-variant-id');
      const p = row.getAttribute('data-product-id');
      row.remove(); // optimistic
      currentCtx.removeVariant(v, p);
      if (!panel.querySelector('[data-wl-row]')) {
        render([], currentCtx);
      }
    }
  });

  document.addEventListener('click', onDocClick, true);
  document.addEventListener('keydown', onKeydown);

  // Rows render NOW, from the local cache — opening never waits on the
  // network. The customer is looking at their list before /me has even left.
  const cached = ctx.getItems();
  render(cached, ctx);

  // Background reconcile. Two jobs: enable Share (share_id only exists
  // server-side), and correct the rows if another device changed the list.
  // Re-rendering is gated on an actual difference so the common case — cache
  // already right — repaints nothing.
  const fingerprint = (items) =>
    items.map((i) => [i.variant_id, i.title, i.image, i.price, i.choose ? 1 : 0].join('~')).join('|');

  ctx.api('/me').then((data) => {
    const serverItems = data.items || [];
    ctx.syncFromServer(serverItems); // cache + hearts + badge follow the server
    if (!panel) return;
    shareId = data.share_id;
    const shareBtn = panel.querySelector('[data-wl-share]');
    if (shareBtn) shareBtn.disabled = !shareId;
    if (fingerprint(ctx.getItems()) !== fingerprint(cached)) {
      render(ctx.getItems(), ctx);
    }
  }).catch(() => {
    // Offline or Worker down: the cached rows stay up. Share stays disabled,
    // remove/add still work optimistically. Nothing to take away.
  });
}

// Registration for the script-tag fallback loader in wishlist.js (used when
// dynamic import() is unavailable/fails in a given browser).
if (typeof window !== 'undefined') {
  window.__wlPopover = { toggle, open };
}
